import { useEffect, useRef, useState } from "react";
import { Button } from "../ui/Button";
import homeService from "../../features/home/services/home.service";
import Sidebar from "./sidebar/Sidebar";
import localization from "../../config/localization";
import { DraggableCarousel } from "../ui/carousel";
import { useNavigate } from "react-router-dom";
import type { ActiveVerticalDetail } from "../../features/home/models";
import { GameVertical } from "../../models/constants/gameVertical";

const SPORTS_VERTICAL = GameVertical.Sports.toLowerCase();
const SAP_VERTICAL = GameVertical.SAP.toLowerCase();

const filterActiveVerticalsForNav = (
  details: ActiveVerticalDetail[],
  userVerticalsCsv: string | null | undefined,
) => {
  const userVerticalsList = (userVerticalsCsv ?? "")
    .toLowerCase()
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

  return details.filter((vertical) => {
    const nameLower = vertical.name.toLowerCase();
    const isSportsIndia =
      nameLower === GameVertical.SportsIndia.toLowerCase() ||
      nameLower === "sports india";

    if (nameLower === SPORTS_VERTICAL) {
      return !userVerticalsList.includes(SPORTS_VERTICAL);
    }
    if (isSportsIndia) {
      return !userVerticalsList.includes(SAP_VERTICAL);
    }
    return !userVerticalsList.some((x) => x === nameLower);
  });
};

export function Navbar({ isAuthenticated = false }: { isAuthenticated: boolean }) {
  const [activeTab, setActiveTab] = useState("exchange");
  const [navItems, setNavItems] = useState<any[]>([]);
  const [showSportsIndia, setShowSportsIndia] = useState(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  const loadNavVerticals = async () => {
    const response = await homeService.fetchActiveVerticals();
    if (!response.isSuccess || !response.data?.details) {
      setNavItems([]);
      return;
    }

    const fetchVerticalIcon = (name: string): string => {
      switch (name) {
        case GameVertical.Exchange:
          return "/icons/ic-exchange.svg";
        case GameVertical.Casino:
          return "/icons/ic-casino.svg";
        case GameVertical.Sports:
          return "/icons/ic-sports.svg";
        default:
          return "/icons/ic-exchange.svg";
      }
    };

    let details = response.data.details.map((vertical) => ({
      ...vertical,
      imageUrl: fetchVerticalIcon(vertical.name),
    }));

    setShowSportsIndia(true);

    if (isAuthenticated) {
      const userVerticals = await homeService.fetchUserVerticals();
      if (userVerticals.isSuccess && userVerticals.data) {
        const userVerticalsList = (userVerticals.data.verticalsCsv ?? "")
          .toLowerCase()
          .split(",")
          .map((s) => s.trim())
          .filter((s) => s.length > 0);

        setShowSportsIndia(!userVerticalsList.includes(SAP_VERTICAL));

        details = filterActiveVerticalsForNav(
          details,
          userVerticals.data.verticalsCsv,
        );
      }
    }

    setNavItems(
      details.map((item) => ({
        id: item.name,
        name: item.name,
        imageUrl: item.imageUrl,
      })),
    );
  };

  const fetchPath = (name: string) => {
    if (name.toLowerCase() === "sports") return "/sportsbookplus";
    if (name.toLowerCase() === "sports-india") return "/bharatsportsbook";
    if (name.toLowerCase() === "exchange") {
      return isAuthenticated ? "exchange/Wickets9" : "exchange";
    }
    return `/${name.toLowerCase()}`;
  };

  const handleNavigateVertical = (name: string) => {
    setActiveTab(name);
    navigate(fetchPath(name));
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const updateScrollState = () => {
      setCanScrollLeft(el.scrollLeft > 0);
      setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    };

    updateScrollState();

    el.addEventListener("scroll", updateScrollState);
    window.addEventListener("resize", updateScrollState);

    return () => {
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [navItems]);

  const scrollLeft = () => {
    scrollRef.current?.scrollBy({ left: -150, behavior: "smooth" });
  };

  const scrollRight = () => {
    scrollRef.current?.scrollBy({ left: 150, behavior: "smooth" });
  };

  useEffect(() => {
    loadNavVerticals();
  }, [isAuthenticated]);

  return (
    <div className="bg-black border-t-[2.5px] border-[#373737] fixed bottom-0 left-0 right-0 z-50">
      <div className="relative flex items-center">

        {canScrollLeft && (
          <div
            onClick={scrollLeft}
            className="absolute left-0 z-10 flex h-full w-10 cursor-pointer items-center justify-center bg-black/70"
          >
            <img
              src="/icons/ic-navbar-arrow.svg"
              alt="left"
              className="size-6"
            />
          </div>
        )}

        <div className="min-w-0 flex-1 overflow-hidden">
          <DraggableCarousel>
            <div
              ref={scrollRef}
              className="flex items-center gap-2 overflow-x-auto scrollbar-hide"
            >
              {navItems.map((item) => (
                <Button
                  key={item.name}
                  onClick={() => handleNavigateVertical(item.name)}
                  className={`flex flex-col items-center justify-center py-3 px-4 min-w-[90px] ${activeTab === item.id
                    ? "text-red-500"
                    : "text-gray-400 hover:text-gray-300"
                    }`}
                >
                  <img
                    className="w-8 h-8 object-contain"
                    src={item.imageUrl}
                    alt={item.name}
                  />
                  <span className="text-[0.65rem] font-semibold whitespace-nowrap">
                    {localization.getString(item.name.toLowerCase())}
                  </span>
                </Button>
              ))}

              {showSportsIndia && (
                <Button
                  onClick={() => handleNavigateVertical("sports-india")}
                  className={`flex flex-col items-center justify-center py-3 px-4 min-w-[90px] ${activeTab === "sports-india"
                    ? "text-red-500"
                    : "text-gray-400 hover:text-gray-300"
                    }`}

                >
                  <img
                    className="w-8 h-8 object-contain"
                    src="/icons/ic-sports-india.svg"
                    alt="sports-india"
                  />
                  <span className="text-[0.65rem] font-semibold whitespace-nowrap">
                    {localization.sportsIndia}
                  </span>
                </Button>
              )}

              <Button
                key="promotion"
                onClick={() => handleNavigateVertical("Promotion")}
                className={`flex flex-col items-center justify-center py-3 px-4 transition-colors duration-200 min-w-[90px] ${activeTab === "promotion"
                  ? "text-red-500"
                  : "text-gray-400 hover:text-gray-300"
                  }`}
              >
                <img
                  className="w-8 h-8 object-contain"
                  src={"/icons/ic-promotion.svg"}
                  alt="promotion"
                />
                <span className="text-[0.65rem] font-semibold tracking-wide whitespace-nowrap">
                  {localization.promotion}
                </span>
              </Button>


            </div>
          </DraggableCarousel>
        </div>

        {canScrollRight && (
          <div
            onClick={scrollRight}
            className="absolute right-[90px] z-10 flex h-full w-10 cursor-pointer items-center justify-center bg-black/70"
          >
            <img
              src="/icons/ic-navbar-arrow.svg"
              alt="right"
              className="size-6 rotate-180"
            />
          </div>
        )}

        <Button
          onClick={() => setIsMenuOpen(true)}
          className="flex min-w-[90px] flex-col items-center justify-center px-4 py-3 text-gray-300"
        >
          <img
            className="w-8 h-8 object-contain"
            src="/icons/ic-menu.svg"
            alt="Menu"
          />
          <span className="text-[0.65rem] font-semibold uppercase">
            {localization.menu}
          </span>
        </Button>

        <Sidebar isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />
      </div>
    </div>
  );
}